import React, { useState, useEffect, useMemo, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Typography, Box, Paper, Link } from '@mui/material';

import AmbientBackground from './AmbientBackground';
import Section from './Section';
import { useHomeHero } from './HomeHeroContext';
import { setSeoMeta } from './seo';

const SLIDE_COUNT = 6;
const HOME_SECTION_SX = { bgcolor: 'transparent', color: 'text.primary' };

function buildHomeSeoMeta(t) {
  const title = `Lett – ${t('app.tagline')}`;
  const description = [t('app.tagline'), t('app.story1'), t('app.story2'), t('app.story3')].join(
    ' '
  );
  return { title, description };
}

export default function App() {
  const { t } = useTranslation();
  const { setHeroVisible } = useHomeHero();
  const heroBrandRef = useRef(null);
  const homeSceneRef = useRef(null);

  useEffect(() => {
    const el = heroBrandRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      ([entry]) => {
        setHeroVisible(entry.isIntersecting);
      },
      { root: null, threshold: 0, rootMargin: '0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [setHeroVisible]);

  useEffect(() => {
    setSeoMeta(buildHomeSeoMeta(t));
  }, [t]);
  const images = useMemo(
    () => Array.from({ length: SLIDE_COUNT }, (_, i) => `/en/${i + 1}.webp`),
    []
  );
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [images]);

  useEffect(() => {
    const scene = homeSceneRef.current;
    if (!scene || typeof window === 'undefined') return undefined;

    let frame = 0;

    const render = () => {
      frame = 0;
      const scrollRatio = Math.min(window.scrollY / Math.max(window.innerHeight, 1), 1.5);
      scene.style.setProperty('--ambient-scroll', scrollRatio.toFixed(4));
    };

    const requestRender = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(render);
    };

    window.addEventListener('scroll', requestRender, { passive: true });
    window.addEventListener('resize', requestRender);
    requestRender();

    return () => {
      window.removeEventListener('scroll', requestRender);
      window.removeEventListener('resize', requestRender);
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, []);

  return (
    <Box ref={homeSceneRef} className="home-scene" sx={{ position: 'relative', isolation: 'isolate' }}>
      <AmbientBackground />
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Section sx={HOME_SECTION_SX}>
        <Box display="flex" alignItems="center" sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box>
            <Box ref={heroBrandRef} display="flex" alignItems="center" mb={3}>
              <Paper
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 16,
                  marginRight: 16,
                  overflow: 'hidden',
                  backgroundColor: '#000000',
                }}
                elevation={5}
              >
                <img src="/lett-logo.webp" alt={t('app.lettAlt')} width={64} height={64} />
              </Paper>
              <Typography variant="h2" component="span">
                Lett
              </Typography>
            </Box>
            <Box mb={3}>
              <Typography variant="h4">{t('app.tagline')}</Typography>
            </Box>
            <Box mb={3} display="flex" gap={2} sx={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <a href="https://apps.apple.com/app/id6752947168" target="_blank" rel="noopener noreferrer" style={{ display: 'block', height: '55px' }}>
                <img src="/appstore.svg" height={55} alt="App Store" style={{ height: '55px', width: 'auto' }} />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.sergmoshkov.lett" target="_blank" rel="noopener noreferrer" style={{ display: 'block', height: '55px' }}>
                <img src="/googleplay.svg" height={55} alt="Google Play" style={{ height: '55px', width: 'auto' }} />
              </a>
            </Box>
            <Box mb={3}>
              <Typography variant="body1">
                {t('app.story1')}
                <br />
                <br />
                {t('app.story2')}
                <br />
                <br />
                {t('app.story3')}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" mb={3}>
              <Link component={NavLink} to="/terms" variant="body1" color="inherit" sx={{ marginRight: 3 }}>
                {t('nav.terms')}
              </Link>
              <Link component={NavLink} to="/privacy" variant="body1" color="inherit">
                {t('nav.privacy')}
              </Link>
            </Box>
          </Box>
          <Box
            width={296}
            height={640}
            sx={{
              borderRadius: 10,
              overflow: 'hidden',
              flex: 'none',
              marginLeft: { xs: 0, sm: 10 },
              marginTop: { xs: 3, sm: 0 },
              transform: 'translateZ(0)',
              position: 'relative',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              animation: 'gradientGlow 3s ease-in-out infinite',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -2,
                left: -2,
                right: -2,
                bottom: -2,
                borderRadius: 12,
                background: 'linear-gradient(45deg, #ff6b6b, #4ecdc4, #45b7d1, #96ceb4, #feca57, #ff9ff3)',
                backgroundSize: '400% 400%',
                animation: 'colorShift 8s ease-in-out infinite',
                zIndex: -1,
                opacity: 0.6,
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: 10,
                background: 'linear-gradient(45deg, rgba(255, 107, 107, 0.1), rgba(78, 205, 196, 0.1), rgba(69, 183, 209, 0.1))',
                backgroundSize: '400% 400%',
                animation: 'colorShift 6s ease-in-out infinite',
                zIndex: -1,
                opacity: 0.3,
              },
            }}
          >
            <Box sx={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
              <Box
                sx={{
                  display: 'flex',
                  height: '100%',
                  transform: `translateX(-${currentImageIndex * 296}px)`,
                  transition: 'transform 0.8s ease-in-out',
                }}
              >
                {images.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={t('app.carouselAlt', { n: index + 1 })}
                    style={{
                      width: 296,
                      height: 640,
                      objectFit: 'cover',
                      flexShrink: 0,
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
        <Box height={24} />
        </Section>
      </Box>
    </Box>
  );
}
